import { useState } from 'react';
import { useLang } from '../context/LanguageContext';
import { useNewsData } from '../context/NewsDataContext';
import { useWhatsAppHelp } from '../context/WhatsAppHelpContext';
import { isNativeApp } from '../utils/platform.js';
import {
  BUSINESS,
  businessAddressBlock,
  businessPhoneDisplay,
  businessTelHref,
  businessWhatsAppHref,
} from '../config/business';
import { WhatsAppSuggestInline } from './WhatsAppSuggestSheet';
import './WhatsAppSuggestSheet.css';
import '../pages/ContactPage.css';

export default function ContactContent() {
  const { t } = useLang();
  const { homeFeed } = useNewsData();
  const { openWhatsAppHelp } = useWhatsAppHelp();
  const [sent, setSent] = useState(false);
  const [form, setForm] = useState({ name: '', phone: '', message: '' });
  const phone = businessPhoneDisplay();

  const handleSubmit = (e) => {
    e.preventDefault();
    const text = `${t.contactWaIntro}\n\n${t.contactWaName}: ${form.name}\n${t.contactWaPhone}: ${form.phone}\n${t.contactWaMessage}: ${form.message}`;
    window.open(businessWhatsAppHref(text), '_blank');
    setSent(true);
  };

  if (sent) {
    if (isNativeApp()) {
      return (
        <div>
          <p className="ios-support__lead">{t.contactSentSuccess}</p>
          <div className="ios-support__group">
            <button type="button" className="ios-support__row" onClick={() => setSent(false)}>
              <span>{t.contactSendAgain}</span>
              <span className="ios-support__go" aria-hidden />
            </button>
          </div>
        </div>
      );
    }
    return (
      <div className="contact-success">
        <span>✅</span>
        <p>{t.contactSentSuccess}</p>
        <button type="button" onClick={() => setSent(false)} className="contact-btn">
          {t.contactSendAgain}
        </button>
      </div>
    );
  }

  if (isNativeApp()) {
    return (
      <div className="ios-contact">
        <p className="ios-support__lead">{t.contactReachUs}</p>
        <div className="ios-support__group">
          <a href={`mailto:${BUSINESS.email}`} className="ios-support__row">
            <span>
              <strong>{t.contactEmailLabel}</strong>
              <em>{BUSINESS.email}</em>
            </span>
            <span className="ios-support__go" aria-hidden />
          </a>
          <a href={businessTelHref()} className="ios-support__row">
            <span>
              <strong>{t.contactPhoneLabel}</strong>
              <em>{phone}</em>
            </span>
            <span className="ios-support__go" aria-hidden />
          </a>
          <button type="button" className="ios-support__row" onClick={() => openWhatsAppHelp('contact_page')}>
            <span>
              <strong>{t.contactWhatsAppLabel}</strong>
              <em>{phone}</em>
            </span>
            <span className="ios-support__go" aria-hidden />
          </button>
        </div>

        <p className="ios-support__lead" style={{ marginTop: 22 }}>{t.contactBusinessTitle}</p>
        <div className="ios-support__group">
          <div className="ios-support__row ios-support__row--static">
            <span>
              <strong>{t.legalEntityLabel}</strong>
              <em>
                {BUSINESS.legalName}
                {' · '}
                {t.tradeNameLabel}: {BUSINESS.tradeName}
              </em>
            </span>
          </div>
          <div className="ios-support__row ios-support__row--static">
            <span>
              <strong>{t.gstLabel}</strong>
              <em>{BUSINESS.gstin}</em>
            </span>
          </div>
          <div className="ios-support__row ios-support__row--static">
            <span>
              <strong>{t.contactAddressLabel}</strong>
              <em>{businessAddressBlock()}</em>
            </span>
          </div>
        </div>

        <p className="ios-support__lead" style={{ marginTop: 22 }}>{t.contactFormTitle}</p>
        <form onSubmit={handleSubmit} className="ios-contact__form">
          <label>
            {t.contactNameLabel}
            <input
              type="text"
              required
              placeholder={t.contactNamePlaceholder}
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
          </label>
          <label>
            {t.contactPhoneLabel}
            <input
              type="tel"
              placeholder={t.contactPhonePlaceholder}
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
            />
          </label>
          <label>
            {t.contactMessageLabel}
            <textarea
              required
              rows={4}
              placeholder={t.contactMessagePlaceholder}
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
            />
          </label>
          <button type="submit" className="ios-contact__send">
            {t.contactSendViaWa}
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="contact-page">
      <div className="contact-reach">
        <p className="contact-reach__label">{t.contactReachUs}</p>
        <div className="contact-reach__actions">
          <a href={`mailto:${BUSINESS.email}`} className="contact-reach__btn">
            <span className="contact-reach__icon" aria-hidden>
              📧
            </span>
            <span className="contact-reach__text">
              <span className="contact-reach__title">{t.contactEmailLabel}</span>
              <span className="contact-reach__value">{BUSINESS.email}</span>
            </span>
          </a>
          <a href={businessTelHref()} className="contact-reach__btn">
            <span className="contact-reach__icon" aria-hidden>
              📞
            </span>
            <span className="contact-reach__text">
              <span className="contact-reach__title">{t.contactPhoneLabel}</span>
              <span className="contact-reach__value contact-reach__value--phone">{phone}</span>
            </span>
          </a>
          <button type="button" className="contact-reach__btn contact-reach__btn--wa" onClick={() => openWhatsAppHelp('contact_page')}>
            <span className="contact-reach__icon" aria-hidden>
              💬
            </span>
            <span className="contact-reach__text">
              <span className="contact-reach__title">{t.contactWhatsAppLabel}</span>
              <span className="contact-reach__value contact-reach__value--phone">{phone}</span>
            </span>
          </button>
        </div>
      </div>

      <div className="contact-page__grid">
        <section className="contact-details" aria-labelledby="contact-details-title">
          <h3 id="contact-details-title" className="contact-details__title">
            {t.contactBusinessTitle}
          </h3>
          <dl className="contact-details__list">
            <div className="contact-details__row">
              <dt>{t.legalEntityLabel}</dt>
              <dd>
                {BUSINESS.legalName}
                <span className="contact-details__sub">
                  {t.tradeNameLabel}: {BUSINESS.tradeName} · {t.businessTypeLabel}: {BUSINESS.constitution}
                </span>
              </dd>
            </div>
            <div className="contact-details__row">
              <dt>{t.gstLabel}</dt>
              <dd>{BUSINESS.gstin}</dd>
            </div>
            <div className="contact-details__row">
              <dt>{t.contactAddressLabel}</dt>
              <dd>{businessAddressBlock()}</dd>
            </div>
            <div className="contact-details__row contact-details__row--intl">
              <dt>{t.contactIntlNoteLabel}</dt>
              <dd>{t.contactIntlNote}</dd>
            </div>
          </dl>
        </section>

        <div className="contact-form-wrap">
          <form onSubmit={handleSubmit} className="contact-form">
            <h3>{t.contactFormTitle}</h3>
            <p className="contact-form__sub">{t.contactFormSub}</p>
            <div className="contact-field">
              <label>{t.contactNameLabel}</label>
              <input
                type="text"
                required
                placeholder={t.contactNamePlaceholder}
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </div>
            <div className="contact-field">
              <label>{t.contactPhoneLabel}</label>
              <input
                type="tel"
                placeholder={t.contactPhonePlaceholder}
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
              />
            </div>
            <div className="contact-field">
              <label>{t.contactMessageLabel}</label>
              <textarea
                required
                rows={5}
                placeholder={t.contactMessagePlaceholder}
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
              />
            </div>
            <button type="submit" className="contact-btn whatsapp">
              {t.contactSendViaWa}
            </button>
          </form>
        </div>
      </div>

      <div className="contact-page__wa-block">
        <WhatsAppSuggestInline placement="contact_page" waTemplates={homeFeed.waTemplates} />
      </div>
    </div>
  );
}
